import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import {
  Search,
  Clear,
  FilterAlt,
  LocalShipping,
  Assignment,
  TableView,
  ViewModule,
  Inventory,
  Scale,
  Event,
  FolderOpen,
  Category,
  List,
  Numbers,
  Link,
  Assessment,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';
import { useLanguage } from '../contexts/LanguageContext';

import procurementData from "../data/hrc_distributed.json";

const ProcurementPage = () => {
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();
  const { currentLanguage } = useLanguage();
  const [filteredData, setFilteredData] = useState(procurementData);
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    location: "",
  });
  const [viewMode, setViewMode] = useState("cards");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const result = procurementData.filter((item) => {
        return (
          (filters.search === "" ||
            (item.type_en && item.type_en.toLowerCase().includes(filters.search.toLowerCase())) ||
            (item.source_en && item.source_en.toLowerCase().includes(filters.search.toLowerCase()))) &&
          (filters.type === "" || item.type_en === filters.type) &&
          (filters.location === "" || item.source_en === filters.location)
        );
      });
      setFilteredData(result);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      type: "",
      location: "",
    });
  };

  // Create unique types with both languages
  const uniqueTypesData = procurementData.reduce((acc, item) => {
    if (item.type_en && !acc.find(t => t.type_en === item.type_en)) {
      acc.push({ type_en: item.type_en, type_ar: item.type_ar });
    }
    return acc;
  }, []);

  // Create unique locations with both languages
  const uniqueLocationsData = procurementData.reduce((acc, item) => {
    if (item.source_en && !acc.find(l => l.source_en === item.source_en)) {
      acc.push({ source_en: item.source_en, source_ar: item.source_ar });
    }
    return acc;
  }, []);

  const uniqueTypes = [...new Set(procurementData.map((item) => item.type_en).filter(Boolean))];
  const uniqueLocations = [...new Set(procurementData.map((item) => item.source_en).filter(Boolean))];

  const totalQuantity = filteredData.reduce(
    (sum, item) => {
      const amount = Number(item.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    },
    0
  );

  const StatsCard = ({ title, value, icon, color }) => (
    <Card className="elegant-card" sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main`, mb: 1 }}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const ProcurementCard = ({ item }) => (
    <Card className="elegant-card animate-fade-in-up" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {getLocalizedValue(item, 'type') || 'N/A'}
          </Typography>
          <Chip
            label={getLocalizedValue(item, 'source') || 'N/A'}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>

        {(item.amount || item.date) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {item.amount && !isNaN(Number(item.amount)) && (
              <Grid item xs={item.date ? 6 : 12}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                    {t("Amount")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {Number(item.amount).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5, color: 'white' }}>
                    {getLocalizedValue(item, 'unit') || t("Unit")}
                  </Typography>
                </Paper>
              </Grid>
            )}
            {item.date && (
              <Grid item xs={item.amount && !isNaN(Number(item.amount)) ? 6 : 12}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("Date")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    {item.date}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t("Source")}: {getLocalizedValue(item, 'source') || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("For")}: {getLocalizedValue(item, 'for') || 'N/A'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box className="animate-fade-in-up">
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h2" 
          className="heading-primary"
          sx={{ 
            textAlign: 'center',
            mb: 2,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("HRC")}
        </Typography>
        <Typography 
          variant="h6" 
          className="text-elegant"
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 3
          }}
        >
          {t("Procurement and Distribution Report")}
        </Typography>

        {/* Information Requests Button */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            component="a"
            href="https://drive.google.com/drive/folders/1Li02YnLUR2Qhx_hXoR4etpHyvr_NsQD2?usp=drive_link"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<FolderOpen />}
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(12, 159, 193, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
                {t("Register of Information Requests")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem', color: 'white' }}>
                {t("Official Responses from Administration")}
              </Typography>
            </Box>
          </Button>
        </Box>
        
        {/* HRC Context */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            maxWidth: '900px', 
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.05) 0%, rgba(77, 182, 209, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              mb: 3
            }}
          >
            {t("hrc_description")}
          </Typography>
          
          {/* Statistics Reference Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/stats/hrc"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(12, 159, 193, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={<Assessment />}
            >
              {t("View Detailed Statistics")}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Items")}
            value={filteredData.length}
            icon={<Inventory />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Received")}
            value={totalQuantity}
            icon={<LocalShipping />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Unique Donors")}
            value={uniqueTypes.length}
            icon={<Assignment />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Sources")}
            value={uniqueLocations.length}
            icon={<Assessment />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FilterAlt sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t("Filter Procurement Items")}
          </Typography>
          <Box>
            <Button
              onClick={() => setViewMode(viewMode === "cards" ? "table" : "cards")}
              variant="outlined"
              sx={{ mr: 2 }}
              startIcon={viewMode === "cards" ? <TableView /> : <ViewModule />}
            >
              {viewMode === "cards" ? t("Table View") : t("Card View")}
            </Button>
            <Button
              onClick={handleReset}
              startIcon={<Clear />}
              variant="outlined"
            >
              {t("Clear Filters")}
            </Button>
          </Box>
        </Box>

        {/* Full-width search field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={t("Search Items or Sources")}
            variant="outlined"
            value={filters.search}
            onChange={(e) => handleChange("search", e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
              sx: {
                height: "56px",
                "& input": {
                  padding: "16.5px 14px",
                },
              },
            }}
          />
        </Box>

        {/* Select fields in a row */}
        <Box sx={{ width: "100%" }}>
          <Grid
            container
            spacing={2}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr",
              },
            }}
          >
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Type")}</InputLabel>
                <Select
                  value={filters.type}
                  label={t("Type")}
                  onChange={(e) => handleChange("type", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Types")}</MenuItem>
                  {uniqueTypesData.map((typeObj) => (
                    <MenuItem key={typeObj.type_en} value={typeObj.type_en}>
                      {getLocalizedValue(typeObj, 'type')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Source")}</InputLabel>
                <Select
                  value={filters.location}
                  label={t("Source")}
                  onChange={(e) => handleChange("location", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Sources")}</MenuItem>
                  {uniqueLocationsData.map((locationObj) => (
                    <MenuItem key={locationObj.source_en} value={locationObj.source_en}>
                      {getLocalizedValue(locationObj, 'source')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            {t("No items match your filters")}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReset}>
            {t("Clear Filters")}
          </Button>
        </Paper>
      ) : viewMode === "cards" ? (
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {filteredData.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <ProcurementCard item={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type (EN)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type (AR)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Source (EN)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Source (AR)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                    "&:hover": { backgroundColor: "action.selected" },
                  }}
                >
                  <TableCell>{row.type_en || 'N/A'}</TableCell>
                  <TableCell>{row.type_ar || 'N/A'}</TableCell>
                  <TableCell>{row.source_en || 'N/A'}</TableCell>
                  <TableCell>{row.source_ar || 'N/A'}</TableCell>
                  <TableCell align="right">{Number(row.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>{row.unit_en || 'N/A'} / {row.unit_ar || 'N/A'}</TableCell>
                  <TableCell>{row.date || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProcurementPage;
